import {
  getIndexPagination,
  getIndexPaginationSearchParams,
  normalizeIndexPagination,
  INDEX_PAGE_SIZES,
} from '@shared/lib/pagination';
import { Button, Flex, Menu, Text } from '@mantine/core';
import { PageContainer } from '@shared/ui';
import {
  IconChevronDown,
  IconClick,
  IconFileExport,
  IconPlus,
  IconWand,
} from '@tabler/icons-react';
import classes from './vacancy-list-page.module.scss';
import {
  getResponsibleUserVacancyFilter,
  RESPONSIBLE_USER_ID_SEARCH_PARAM,
  useVacancyGenerateModal,
  VacancyList,
  VacancyListFilters,
  type VacancyFiltersQuery,
} from '@features/vacancy';
import { Link, useSearchParams } from 'react-router';
import { useEffect, useState } from 'react';
import { useExportVacancy, useGetVacancies } from '@entities/vacancy';
import { PaginateType, type SortQuery } from '@shared/types';
import { useDataExport } from '@shared/ui/modal/hooks/useDataExport';
import type { ExportDataFunction } from '@shared/ui/modal/hooks/useDataExport/types';

export const VacancyListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<VacancyFiltersQuery>(() =>
    getResponsibleUserVacancyFilter(
      searchParams.get(RESPONSIBLE_USER_ID_SEARCH_PARAM)
    )
  );
  const [sort, setSort] = useState<SortQuery>({ id: 'desc' });
  const { page, pageSize } = getIndexPagination(searchParams);

  useEffect(() => {
    const normalized = normalizeIndexPagination(searchParams);
    if (normalized.toString() !== searchParams.toString()) {
      setSearchParams(normalized, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const setPage = (nextPage: number) => {
    setSearchParams(
      getIndexPaginationSearchParams(searchParams, nextPage, pageSize)
    );
  };
  const setPageSize = (size: number) => {
    setSearchParams(getIndexPaginationSearchParams(searchParams, 1, size));
  };
  const { mutateAsync: exportVacancy, isPending } = useExportVacancy();
  const { openVacancyGenerateModal } = useVacancyGenerateModal();

  const { openChangeDataExport } = useDataExport({
    text: 'Экспортируйте все вакансии или созданные в заданный период  ',
    title: 'Экспорт вакансий',
    type: 'vacancy',
    exportData: exportVacancy as unknown as ExportDataFunction,
    isPending,
  });

  const {
    data: vacancies,
    isLoading,
    isFetching,
  } = useGetVacancies({
    filter,
    sort,
    perPage: pageSize,
    page,
    paginate: PaginateType.DEFAULT,
  });

  const handleSortChange = (field: string, direction?: 'asc' | 'desc') => {
    if (!direction) {
      setSort({ id: 'desc' });
      return;
    }

    setSort({
      [field]: direction,
    });
  };

  return (
    <PageContainer
      title='Вакансии'
      actionButton={
        <Flex gap='0.5rem'>
          <Button
            onClick={openChangeDataExport}
            variant='default'
            leftSection={<IconFileExport />}
            size='md'
          >
            Экспорт
          </Button>
          <Menu width={254}>
            <Menu.Target>
              <Button
                leftSection={<IconPlus />}
                rightSection={<IconChevronDown />}
                className={classes.addUserButton}
                size='md'
              >
                Создать вакансию
              </Button>
            </Menu.Target>
            <Menu.Dropdown p={2}>
              <Menu.Item
                component={Link}
                to='/vacancy-list/create'
                leftSection={<IconClick />}
              >
                <Text size='sm'>Ручное создание</Text>
              </Menu.Item>
              <Menu.Item
                leftSection={<IconWand />}
                onClick={openVacancyGenerateModal}
              >
                <Text size='sm'>Сгенерировать вакансию</Text>
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>
      }
    >
      <VacancyListFilters setFilter={setFilter} pageSize={pageSize} />

      <VacancyList
        vacancies={vacancies?.data || []}
        totalRecords={vacancies?.pagination.total}
        page={page}
        setPage={setPage}
        onRecordsPerPageChange={setPageSize}
        recordsPerPageOptions={INDEX_PAGE_SIZES}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isFilledFilters={Boolean(Object.keys(filter).length)}
        onSortChange={handleSortChange}
        currentSort={sort}
        isLoading={isLoading || isFetching}
      />
    </PageContainer>
  );
};
